import { useState } from 'react';
import { useGlobalStore } from '../../../application/store/store.tsx';
import AvailableDrugs from '../components/AvailableDrugs.tsx';
import TextField from '@mui/material/TextField';
import { axiosInstance } from '../../../common/axiosInstans';
import Visibility from '../../../common/components/Visibility';
import {
    TransactionOverview,
    TransactionType,
} from '../../../application/types/transactionType.ts';
import { isSignButtonDisabled } from '../../../common/utils/transactionValidation.tsx';
import {
    Alert,
    Button,
    Checkbox,
    IconButton,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Box,
} from '@mui/material';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const DrugInventoryManagement = () => {
    const [
        acceptSaldoMismatchInventoryCheckbox,
        setAcceptSaldoMismatchInventoryCheckbox,
    ] = useState(false);
    const [behallning, setBehallning] = useState('');

    const [warningMessage, setWarningMessage] = useState<string | null>(null);
    const [showWarning, setShowWarning] = useState(false);
    const [checkboxWarningMessage, setCheckboxWarningMessage] = useState<
        string | null
    >(null);
    const [showCheckboxWarning, setShowCheckboxWarning] = useState(false);

    const {
        selectedStorage,
        inventoryRecordOverview,
        selectedDrug,
        setSelectedDrug,
        inventoryRecords,
        setInventoryRecords,
        inventoryList,
        setInventoryList,
        showSigningInInventory,
    } = useGlobalStore();

    const [searchTermDrugs, setSearchTermDrugs] = useState<string>('');

    const handleSelectDrug = (drug) => {
        const formattedDrugValue = `${drug.drugName ?? ''} (${drug.concentration ?? ''} ${drug.concentrationUnit ?? ''}, ${drug.quantity ?? ''}${drug.quantityUnit ?? ''})`;
        setSelectedDrug(drug);
        setSearchTermDrugs(formattedDrugValue);
    };

    const resetBehallningField = () => setBehallning('');

    const addItem = async () => {
        if (!selectedDrug) {
            setWarningMessage('Välj ett läkemedel innan du sparar.');
            setShowWarning(true);
            return;
        }

        if (!behallning || isNaN(Number(behallning))) {
            setWarningMessage('Ange en giltig behållning.');
            setShowWarning(true);
            return;
        }
        setWarningMessage(null);
        setShowWarning(false);

        try {
            const response = await axiosInstance.get(
                `/currentquantity/${selectedStorage.id}/${selectedDrug.id}`
            );
            const currentQuantity = response.data.quantity;

            const mismatch = Number(currentQuantity) !== Number(behallning);

            if (!acceptSaldoMismatchInventoryCheckbox && mismatch) {
                setCheckboxWarningMessage(
                    'Saldot stämmer inte överens med det som finns i systemet. Klicka i rutan för att godkänna nytt saldo.'
                );
                setShowCheckboxWarning(true);
                return;
            }
            setCheckboxWarningMessage(null);
            setShowCheckboxWarning(false);

            const postData = {
                storageId: selectedStorage.id,
                drugId: selectedDrug.id,
                quantity: Number(behallning),
            };

            await axiosInstance.post(`/currentquantity`, postData);

            const drugDescription = `${selectedDrug.drugName} (${selectedDrug.concentration} ${selectedDrug.concentrationUnit}, ${selectedDrug.quantity}${selectedDrug.quantityUnit})`;
            const newDrug = {
                drugName: drugDescription,
                antal: Number(behallning),
                mismatch: mismatch, // Track mismatch state
            };

            const existingDrugIndex = inventoryList.findIndex(
                (item) => item.drugName === newDrug.drugName
            );

            if (existingDrugIndex !== -1) {
                const updatedList = inventoryList.map((item, index) => {
                    if (index === existingDrugIndex) {
                        return {
                            ...item,
                            antal: Number(behallning),
                            mismatch: mismatch,
                        };
                    }
                    return item;
                });
                setInventoryList(updatedList);
            } else {
                setInventoryList([...inventoryList, newDrug]);
            }

            const existingRecordIndex = inventoryRecords.findIndex(
                (record) => record.drugId === selectedDrug.id
            );

            if (existingRecordIndex !== -1) {
                const updatedRecords = inventoryRecords.map((record, index) => {
                    if (index === existingRecordIndex) {
                        return {
                            ...record,
                            remainingQuantityAccordingToUser:
                                Number(behallning),
                        };
                    }
                    return record;
                });
                setInventoryRecords(updatedRecords);
            } else {
                const newRecord = {
                    drugId: selectedDrug.id,
                    remainingQuantityAccordingToUser: Number(behallning),
                };
                setInventoryRecords([...inventoryRecords, newRecord]);
            }

            resetBehallningField();
            setWarningMessage(null);
            setShowWarning(false);
            setCheckboxWarningMessage(null);
            setShowCheckboxWarning(false);
            setAcceptSaldoMismatchInventoryCheckbox(false);
        } catch (error) {
            console.error(
                'Fel vid hämtning eller postning av läkemedel:',
                error
            );
        }
    };

    const deleteItem = (index) => {
        setInventoryList(inventoryList.filter((_, i) => i !== index));
        setInventoryRecords(inventoryRecords.filter((_, i) => i !== index));
    };
    const signButtonDisabled = isSignButtonDisabled(
        TransactionType.Crosscheck,
        selectedDrug,
        selectedStorage,
        undefined,
        undefined,
        undefined,
        undefined
    );

    return (
        <>
            {/* Hide input fields when in double sign mode */}
            <Visibility
                visible={
                    inventoryRecordOverview !==
                    TransactionOverview.doubleSignOverview
                }
            >
                {/* Drug Search */}
                <div className="flex flex-col items-center mb-4">
                    <Box
                        sx={{
                            width: {
                                sm: '24rem',
                                xs: '24rem',
                                lg: '24rem',
                                md: '24rem',
                            },
                            maxWidth: '100%',
                            mx: 'auto',
                        }}
                    >
                        <AvailableDrugs
                            searchTerm={searchTermDrugs}
                            setSearchTerm={setSearchTermDrugs}
                            handleSelect={handleSelectDrug}
                            storageId={selectedStorage?.id}
                        />
                    </Box>
                </div>
                {/* Inventory Input */}

                <TextField
                    id="behallningDrugManagement"
                    label="Behållning"
                    type="number"
                    value={behallning ?? ''}
                    inputProps={{ min: 0 }}
                    onKeyDown={(e) => {
                        if (e.key === '-' || e.key.toLowerCase() === 'e') {
                            e.preventDefault();
                        }
                    }}
                    onChange={(e) => {
                        const v = e.target.value;
                        const clamped =
                            v === '' ? '' : String(Math.max(0, Number(v)));
                        setBehallning(clamped);
                    }}
                    variant="outlined"
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment
                                position="start"
                                style={{ color: '#aaa' }}
                            >
                                {selectedDrug
                                    ? `${selectedDrug.quantity} ${selectedDrug.quantityUnit} x`
                                    : ''}
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Warnings */}

                <Visibility visible={showWarning}>
                    <Alert severity="warning" className="w-full mb-4">
                        {warningMessage}
                    </Alert>
                </Visibility>

                <Visibility visible={showCheckboxWarning}>
                    <Alert severity="warning" className="w-full mb-4">
                        <div className="flex items-center gap-2">
                            <Checkbox
                                checked={acceptSaldoMismatchInventoryCheckbox}
                                onChange={() =>
                                    setAcceptSaldoMismatchInventoryCheckbox(
                                        !acceptSaldoMismatchInventoryCheckbox
                                    )
                                }
                            />
                            <span className="text-sm">
                                {checkboxWarningMessage}
                            </span>
                        </div>
                    </Alert>
                </Visibility>

                {/* Save Button */}

                <Button
                    disabled={
                        !behallning || !selectedDrug || signButtonDisabled
                    }
                    variant="contained"
                    onClick={addItem}
                    sx={{ mb: 2 }}
                >
                    Spara i inventeringslistan
                </Button>
            </Visibility>

            {/* Inventory Table */}
            {inventoryList.length > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                        marginBottom: '1rem',
                    }}
                >
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell className="font-semibold">
                                        Läkemedel
                                    </TableCell>
                                    <TableCell className="font-semibold">
                                        Antal
                                    </TableCell>
                                    <TableCell width="48px"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {inventoryList.map((item, index) => (
                                    <TableRow
                                        key={index}
                                        className="odd:bg-gray-50"
                                    >
                                        <TableCell className="break-words">
                                            <div className="flex items-center gap-2">
                                                <VaccinesIcon className="flex-shrink-0" />
                                                <span className="text-sm">
                                                    {item.drugName}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <span>{item.antal}</span>
                                                {item.mismatch && (
                                                    <Tooltip title="Inventeringen stämmer inte överens med det som finns i systemet.">
                                                        <WarningAmberIcon
                                                            color="warning"
                                                            fontSize="small"
                                                        />
                                                    </Tooltip>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    deleteItem(index)
                                                }
                                                aria-label="delete"
                                                disabled={
                                                    inventoryRecordOverview ===
                                                    TransactionOverview.doubleSignOverview
                                                }
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
            <Visibility
                visible={
                    showSigningInInventory &&
                    inventoryRecordOverview !==
                        TransactionOverview.doubleSignOverview
                }
            >
                <Alert severity="info">
                    När du är klar med att inventera måste du signera för att
                    spara.
                </Alert>
            </Visibility>
        </>
    );
};

export default DrugInventoryManagement;
